/**
 * `@dxf-ai-editor/cad-core` — typed per-kind entity bounds (DEFUSE S10b).
 *
 * Axis-aligned WCS (model-space) bounds computed directly from the
 * canonical `DrawingEntity` fields — NOT the `EntityPreview.properties`
 * string bag. Reference sources: Autodesk DXF reference (per-entity group
 * codes), `ezdxf` (bounding-box conventions), and this repo's existing
 * per-kind reference math:
 *   - `apps/web/src/app/scene/entityBounds.ts` (legacy string-bag bounds —
 *     the kind coverage + edge-case reference named by the task).
 *   - `drawing-model/viewportMembership.ts::entityModelBounds` (the ALREADY
 *     TYPED bounds math for Line/Point/Circle/LWPOLYLINE — built for the
 *     viewport-clip membership query) — REUSED verbatim below rather than
 *     re-derived, so the two typed bounds computations can never drift (one
 *     truth for e.g. bulge-arc math). Arc/Hatch/MText/Text/Block are handled
 *     DIRECTLY by this package instead (see below — Codex #467 P2 / PR #784
 *     round 3 P1): that module's Arc `% 360` fold collapses a full circle to
 *     a point, its Hatch box is a coarse `center ± radius` viewport-cull and
 *     its Text box is the anchor pair only (no glyph extent), all too lossy
 *     for a select-hit / extents bounds.
 *
 * Every other kind (Arc/Ellipse/Spline/Hatch get an EXACT treatment here,
 * tighter than that module's deliberately-conservative viewport-cull
 * approximation; Dimension/Multileader/Revision Cloud/Wipeout/Rectangular
 * Associative Array/Viewport/MText have no precedent there at all, or —
 * MText — only a degenerate insertion-point-only one) is implemented in
 * `./leafBounds.ts`
 * (split out so `blockGeometry.ts`'s own per-kind block-DEFINITION-child
 * resolution can reuse the exact same per-kind math without a
 * `bounds.ts` ↔ `blockGeometry.ts` import cycle).
 */

import {
  entityModelBounds,
  tableAsBlockReference,
  ALWAYS_INCLUDE,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import type { Aabb } from "./common";
import { arcBoundsDeg, unionBounds } from "./common";
import { heavyPolylineDrawnBounds } from "./heavyPolylineDrawnBounds";
import { lwpolylineDrawnBounds } from "./lwpolylineDrawnBounds";
import {
  arrayRectBounds,
  bulgePolylineBounds,
  dimensionBounds,
  ellipseBounds,
  hatchBounds,
  mtextBounds,
  multileaderBounds,
  splineBounds,
  textBounds,
  viewportBounds,
  wipeoutBounds,
} from "./leafBounds";
import {
  multileaderBlockContentBounds,
  resolveBlockInsertBounds,
  type BlockLookupContext,
} from "./blockGeometry";

/** Re-exported so `index.ts` (the public barrel) has a single import site. */
export type { BlockLookupContext } from "./blockGeometry";

/**
 * Typed axis-aligned bounds of an entity, read directly from the canonical
 * `DrawingEntity`. `ctx.blockDefinitions` (optional) lets a `Block` (INSERT)
 * entity resolve its referenced definition's real footprint — see
 * {@link resolveBlockInsertBounds}; without it, a `Block` returns its
 * insertion-point-only degenerate box.
 */
export function getEntityBounds(
  entity: DrawingEntity,
  ctx?: BlockLookupContext,
): Aabb | null {
  switch (entity.type) {
    case "Arc":
      // Dispatched to cad-core's OWN `arcBoundsDeg` (via `sweepDeg`) rather
      // than `entityModelBounds`'s inline `arcBounds`, whose `(end-start) %
      // 360` fold collapses a FULL-circle arc (startAngle 0 / endAngle 360) to
      // a single point — `sweepDeg` treats a full/over-full turn as 360 and
      // frames every crossed cardinal (Codex #467 P2).
      return arcBoundsDeg(entity.center.x, entity.center.y, entity.radius, entity.startAngle, entity.endAngle);
    case "Ellipse":
      return ellipseBounds(entity);
    case "Spline":
      return splineBounds(entity);
    case "Dimension":
      // `ctx.dimensionStyles` (when supplied) resolves the entity's NAMED
      // DIMSTYLE row — the SAME middle tier the block-child Dimension arm of
      // `blockDefinitionWalk.ts` now uses, so a top-level DIMENSION and the
      // identical one nested in a block definition report the same padded
      // hull (Codex PR #784 round 3 P1).
      return dimensionBounds(entity, ctx?.dimensionStyles);
    case "Multileader": {
      // Leader/vertex/MText-content extent (leaf math). BLOCK content
      // (contentType 1) additionally spans the referenced block's flattened
      // footprint at `textLocation` — resolved here (not in `leafBounds.ts`,
      // which must stay free of the `blockGeometry.ts` import that would form a
      // cycle) and UNION'd in when a `blockDefinitions` context is available
      // (Codex #467 P2 "MLEADER block-content bounds"). Absent context ⇒ the
      // leader/anchor bounds stand (conservative — never shrink).
      const leaf = multileaderBounds(entity);
      if (entity.contentType === 1 && ctx?.blockDefinitions) {
        const content = multileaderBlockContentBounds(
          entity,
          ctx.blockDefinitions,
          ctx.definitionBoundsMemo,
          undefined,
          ctx.resolveDynamicChildren,
          ctx.dimensionStyles,
        );
        if (content) return leaf ? unionBounds(leaf, content) : content;
      }
      return leaf;
    }
    case "Revision Cloud":
      return bulgePolylineBounds(entity.vertices, entity.closed);
    case "Polyline":
      // `entityModelBounds` (the default arm below) frames the CENTRELINE — it
      // walks the entity's own local points, bows its bulge arcs in the OCS
      // plane and extrudes the group-39 thickness. It knows nothing about a
      // WIDTH, so a constant-width-4 polyline on (0,0) → (10,0) reported
      // y ∈ [0, 0] for a ribbon the canvas draws across y ∈ [-2, +2], and
      // `getSelectGeometry` — which carries THIS box together with the band
      // points `./heavyPolylineDrawnShape.ts` returns — handed out an object
      // whose own points sat outside its own bounds.
      //
      // The drawn silhouette SWEPT along the thickness vector is therefore
      // union'd in, through the one truth `./heavyPolylineDrawnBounds.ts`
      // — the SAME call `blockDefinitionWalk.ts`' block-child `"Polyline"` arm
      // makes, so a polyline and the identical polyline nested in a block
      // definition can never report different footprints (@codex PR #785
      // round 9, F2). See that module for why a union of the two boxes is not
      // the Minkowski sum an oblique extrusion needs (round 9, F1).
      return heavyPolylineDrawnBounds(entity);
    case "LWPOLYLINE":
      // Split out of the `default` arm below for the SAME reason the
      // `"Polyline"` arm above was: `entityModelBounds` → `lwpolylineBounds`
      // frames the CENTRELINE — exactly, bulge arcs included — and reads
      // neither group 43 nor groups 40 / 41, so a constant-width-4 LWPOLYLINE
      // on (0,0) → (10,0) reported y ∈ [0, 0] for a ribbon the canvas draws
      // across y ∈ [-2, +2]. The DRAWN band is union'd in through the one
      // truth `./lwpolylineDrawnBounds.ts` — the SAME call
      // `blockDefinitionWalk.ts`' block-child `"LWPOLYLINE"` arm makes, so an
      // LWPOLYLINE and the identical one nested in a block definition can
      // never report different footprints (@codex PR #785 round 11, F1).
      return lwpolylineDrawnBounds(entity);
    case "Wipeout":
      return wipeoutBounds(entity);
    case "Rectangular Associative Array":
      return arrayRectBounds(entity);
    case "Viewport":
      return viewportBounds(entity);
    case "MText":
      return mtextBounds(entity);
    case "Text":
      // The rendered glyph box (one-truth `drawing-model::textLocalCorners`)
      // ∪ the anchor pair — NOT `entityModelBounds`'s anchor-only pair, which
      // bounds a label by its insertion point alone (Codex PR #784 round 3
      // P1; same treatment `blockDefinitionWalk.ts::childEntityBounds` applies to a
      // block-definition TEXT child).
      return textBounds(entity);
    case "Hatch":
      // Dispatched to cad-core's OWN `hatchBounds` (exact edge-curve span via
      // the one-truth `edgeToCurve`) rather than `entityModelBounds`'s coarse
      // `center ± radius` viewport-cull box, which inflated a quarter-arc
      // boundary to the full circle (Codex #467 P2).
      return hatchBounds(entity);
    case "Block":
      return resolveBlockInsertBounds(entity, ctx);
    case "Table":
      // An `ACAD_TABLE` IS a block reference (its first subclass is
      // `AcDbBlockReference`, group 2 names the anonymous `*T<N>` block
      // carrying the rendered table graphics), so its footprint is that
      // block's footprint — resolved through the SAME insert chain an INSERT
      // uses, from the ONE shared projection `drawing-model` already defines
      // for the WCS-bounds walk and the metadata index
      // (`tableAsBlockReference`; never a second "what does a table occupy"
      // derivation, and never a hard-coded box).
      //
      // Dispatched HERE rather than through the `entityModelBounds` default
      // arm below because THAT function resolves a block reference from the
      // pre-built `blockDefBounds` / `blockBasePoints` maps the membership
      // index owns, which this package's callers do not supply — cad-core's
      // context is `ctx.blockDefinitions`. Without this arm a table reported
      // NO bounds at all, and `getSelectGeometry` bailed on the `null` before
      // it could ever reach its Table geometry (Codex PR #785 round 1, F2).
      return resolveBlockInsertBounds(tableAsBlockReference(entity), ctx);
    default: {
      // Line / Point / Circle / OLE Frame — the kinds
      // `entityModelBounds` already computes exactly (reused, not re-derived;
      // see the module doc).
      const resolved = entityModelBounds(entity);
      if (resolved === null || resolved === ALWAYS_INCLUDE) return null;
      return { minX: resolved.minX, minY: resolved.minY, maxX: resolved.maxX, maxY: resolved.maxY };
    }
  }
}
